import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Zap,
  MessageCircle,
  Mail,
  Phone,
  StickyNote,
  Check,
  X,
  Clock,
  Flame,
  ExternalLink,
  TrendingUp,
  CreditCard,
  Copy,
  Send,
  AlertTriangle,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { apiClient, type RecoveryLead } from '../lib/api.js';
import { Card, CardContent } from '../components/ui/Card.js';
import { Badge } from '../components/ui/Badge.js';
import { CallDialog } from '../components/CallDialog.js';
import {
  cn,
  formatBRL,
  formatPercent,
  temperatureColor,
  temperatureLabel,
} from '../lib/utils.js';

/**
 * Templates por status do abandono. Patricia copia/envia direto.
 * {firstName} → primeiro nome do cliente. {value} → R$ valor da tentativa.
 */
const TEMPLATES: Record<string, { label: string; emoji: string; text: string }> = {
  pix_created: {
    label: 'PIX criado · lembrete suave',
    emoji: '🟡',
    text: 'Oi {firstName}! Aqui é a Patricia da NextBoot AI 💛 Vi que você gerou o PIX de {value} pra entrar no NextBoot, mas o pagamento ainda não foi confirmado. Quer que eu te ajude a finalizar agora? Posso gerar um novo PIX ou tirar qualquer dúvida que você tenha 🙌',
  },
  pix_expired: {
    label: 'PIX expirado · reabrir checkout',
    emoji: '🔴',
    text: 'Oi {firstName}, aqui é a Patricia da NextBoot AI 👋 Seu PIX de {value} expirou — quer que eu te mande um novo link pronto pra pagar em 30 segundos? Posso liberar com a mesma oferta especial 🙏',
  },
  bank_slip_created: {
    label: 'Boleto criado · lembrete',
    emoji: '🟡',
    text: 'Oi {firstName}! Patricia aqui da NextBoot AI 👋 Seu boleto de {value} já foi gerado. Se preferir, posso trocar pra PIX que aprova na hora? Me avisa que eu te envio o link 🙌',
  },
  refused: {
    label: 'Cartão recusado · oferecer ajuda',
    emoji: '🔴',
    text: 'Oi {firstName}! Aqui é a Patricia da NextBoot AI 💛 Vi que seu cartão não passou agora pra entrar no NextBoot. Acontece bastante — quer tentar com outro cartão, parcelar diferente ou pagar via PIX? Posso te ajudar agora mesmo 🙏',
  },
  waiting_payment: {
    label: 'Aguardando pagamento · check rápido',
    emoji: '🟡',
    text: 'Oi {firstName}, é a Patricia da NextBoot AI 👋 Vi que o checkout de {value} tá aberto mas o pagamento não veio ainda. Tá tudo certo? Se precisar de ajuda pra finalizar, é só me chamar aqui!',
  },
  default: {
    label: 'Mensagem genérica · recuperação',
    emoji: '💬',
    text: 'Oi {firstName}! Aqui é a Patricia da NextBoot AI 💛 Vi que você começou seu cadastro mas não finalizou. Tá tudo certo? Posso te ajudar a concluir agora ou tirar alguma dúvida sobre o NextBoot 🙌',
  },
};

function pickTemplate(status: string | undefined, isReturningCustomer: boolean): { label: string; emoji: string; text: string } {
  // Cliente recorrente recebe versão mais íntima
  if (isReturningCustomer && status) {
    const s = status.toLowerCase();
    if (s.includes('pix_expired')) {
      return {
        label: 'Cliente · PIX expirou',
        emoji: '⭐',
        text: 'Oi {firstName}! 👋 Vi que o PIX de {value} da sua nova compra expirou. Quer que eu te mande o link de novo? Bora destravar isso! 🙌',
      };
    }
    if (s.includes('pix')) {
      return {
        label: 'Cliente · PIX pendente',
        emoji: '⭐',
        text: 'Oi {firstName}! 👋 Notei que seu PIX de {value} ainda não foi pago — tudo bem? Se precisar de um novo link ou ajuda, é só falar comigo 🙌',
      };
    }
    if (['refused', 'declined', 'failed', 'rejected'].includes(s)) {
      return {
        label: 'Cliente · cartão recusado',
        emoji: '⭐',
        text: 'Oi {firstName}! 👋 Vi que seu cartão não passou agora. Acontece — quer tentar com outro ou via PIX? Como você já é cliente, libero rapidinho 🙌',
      };
    }
  }
  if (!status) return TEMPLATES.default;
  const s = status.toLowerCase();
  if (s.includes('pix_expired') || s.includes('pix_recurring')) return TEMPLATES.pix_expired;
  if (s.includes('pix')) return TEMPLATES.pix_created;
  if (s.includes('bank_slip') || s.includes('boleto')) return TEMPLATES.bank_slip_created;
  if (['refused', 'declined', 'failed', 'rejected'].includes(s)) return TEMPLATES.refused;
  if (s.includes('waiting') || s === 'pending') return TEMPLATES.waiting_payment;
  return TEMPLATES.default;
}

function renderTemplate(text: string, firstName: string, valueBRL: string): string {
  return text.replace(/\{firstName\}/g, firstName).replace(/\{value\}/g, valueBRL);
}

const stageConfig: Record<string, { label: string; color: string; description: string }> = {
  checkout_started: {
    label: 'Checkout iniciado',
    color: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
    description: 'Cliente começou checkout mas não finalizou',
  },
  payment_pending: {
    label: 'Pgto. pendente',
    color: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    description: 'PIX ou boleto gerado, aguardando pagamento',
  },
  payment_failed: {
    label: 'Pgto. falhou',
    color: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    description: 'Cartão recusado, precisa nova tentativa',
  },
};

function formatTimeSince(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function slaColor(seconds: number): string {
  if (seconds < 1800) return 'text-emerald-400'; // <30min
  if (seconds < 7200) return 'text-amber-400'; // <2h
  return 'text-rose-400'; // >2h
}

function urgencyColor(urgency: number): string {
  if (urgency >= 0.75) return 'bg-rose-500/20 text-rose-300 border-rose-500/40';
  if (urgency >= 0.55) return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
}

function LeadCard({ lead, onTouch, onDismiss, onStartCall }: {
  lead: RecoveryLead;
  onTouch: (id: string, kind: 'whatsapp' | 'email' | 'call' | 'note') => void;
  onDismiss: (id: string, reason: 'recovered' | 'lost') => void;
  onStartCall: (lead: RecoveryLead) => void;
}): JSX.Element {
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [aiWarning, setAiWarning] = useState<string | null>(null);

  const aiDraft = useMutation({
    mutationFn: () => apiClient.aiDraftMessage(lead.id, { tone: 'amigavel' }),
    onSuccess: (r) => {
      setAiMessage(r.message);
      setAiWarning(r.warning ?? null);
    },
  });

  const stage = stageConfig[lead.stage] ?? {
    label: lead.stage,
    color: 'bg-slate-500/15 text-slate-300',
    description: '',
  };
  const probPurchase = Number(lead.scores?.prob_purchase_24h ?? 0);
  const lastValue = Number(lead.lastAttemptCents ?? 0);
  const sla = lead.secondsSinceStage;

  return (
    <Card className="relative">
      {lead.urgency >= 0.75 && (
        <div className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-bl-md rounded-tr-md flex items-center gap-1">
          <Flame className="h-2.5 w-2.5" /> Urgente
        </div>
      )}
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <Link
              to={`/contatos/${lead.contactId}`}
              className="font-semibold hover:text-primary inline-flex items-center gap-1"
            >
              {lead.contactName ?? '—'}
              <ExternalLink className="h-3 w-3 opacity-50" />
            </Link>
            <div className="text-xs text-muted-foreground truncate">
              {lead.contactEmail} · {lead.contactPhone ?? '—'}
            </div>
            {(lead.geoCity || lead.geoState) && (
              <div className="text-[10px] text-muted-foreground">
                {[lead.geoCity, lead.geoState].filter(Boolean).join(' / ')}
              </div>
            )}
          </div>
          <span
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border tabular-nums',
              urgencyColor(lead.urgency),
            )}
          >
            <TrendingUp className="h-3 w-3" />
            {(lead.urgency * 100).toFixed(0)}
          </span>
        </div>

        {/* Stage + temperature + UTM + cliente status */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className={cn('text-[10px]', stage.color)}>
            {stage.label}
          </Badge>
          <Badge
            variant="outline"
            className={cn('text-[10px]', temperatureColor[lead.temperature])}
          >
            {temperatureLabel[lead.temperature] ?? lead.temperature}
          </Badge>
          {lead.priorOrders > 0 ? (
            <Badge
              variant="outline"
              className="text-[10px] bg-blue-500/15 text-blue-300 border-blue-500/30"
              title={`LTV histórico: ${formatBRL(Number(lead.priorLtvCents ?? 0))} · última compra ${lead.lastPurchaseAt ? new Date(lead.lastPurchaseAt).toLocaleDateString('pt-BR') : '—'}`}
            >
              ⭐ Cliente · {lead.priorOrders} {lead.priorOrders === 1 ? 'compra' : 'compras'}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-[10px] bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
            >
              🆕 Primeira compra
            </Badge>
          )}
          {(lead.openAttempts ?? 0) > 1 && (
            <Badge
              variant="outline"
              className="text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/30"
              title={`${lead.openAttempts} tentativas abandonadas em aberto desse mesmo contato`}
            >
              {lead.openAttempts}× tentativas
            </Badge>
          )}
          {lead.utmSource && (
            <Badge variant="outline" className="text-[10px]">
              {lead.utmSource}{lead.utmCampaign ? ` · ${lead.utmCampaign}` : ''}
            </Badge>
          )}
        </div>

        {/* SLA + valor + scores */}
        <div className="grid grid-cols-3 gap-3 py-2 border-y border-border/40 text-xs">
          <div>
            <div className="text-[10px] text-muted-foreground uppercase">SLA</div>
            <div className={cn('font-bold tabular-nums', slaColor(sla))}>
              <Clock className="inline h-3 w-3 mr-0.5" />
              {formatTimeSince(sla)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase">Valor</div>
            <div className="font-bold tabular-nums">
              <CreditCard className="inline h-3 w-3 mr-0.5" />
              {lastValue > 0 ? formatBRL(lastValue) : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-muted-foreground uppercase">Comprar 24h</div>
            <div className="font-bold tabular-nums">
              <Zap className="inline h-3 w-3 mr-0.5" />
              {formatPercent(probPurchase)}
            </div>
          </div>
        </div>

        {/* Touchpoint history badges — antes do template */}
        {((lead.whatsappCount ?? 0) > 0 || (lead.callCount ?? 0) > 0 || lead.hasOpenConversation) && (
          <div className="flex flex-wrap gap-1.5 text-[10px]">
            {lead.hasOpenConversation && (
              <Badge variant="outline" className="bg-cyan-500/15 text-cyan-300 border-cyan-500/30">
                💬 Em conversa
              </Badge>
            )}
            {(lead.whatsappCount ?? 0) > 0 && (
              <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                📨 {lead.whatsappCount} WA{lead.whatsappLastAt && ` · ${formatTimeSince(Math.floor((Date.now() - new Date(lead.whatsappLastAt).getTime()) / 1000))}`}
              </Badge>
            )}
            {(lead.callCount ?? 0) > 0 && (
              <Badge variant="outline" className="bg-violet-500/15 text-violet-300 border-violet-500/30">
                📞 {lead.callCount} ligaç{lead.callCount === 1 ? 'ão' : 'ões'}{(lead.callContactCount ?? 0) > 0 ? ' · falou' : ''}
              </Badge>
            )}
          </div>
        )}

        {/* Template Whatsapp sugerido pelo status (com opção de gerar via IA) */}
        {(() => {
          const firstName = (lead.contactName ?? 'cliente').split(' ')[0];
          const valueStr = lastValue > 0 ? formatBRL(lastValue) : 'R$ 0';
          const isReturning = lead.priorOrders > 0;
          const tpl = pickTemplate(lead.abandonStatus, isReturning);
          const templateMsg = renderTemplate(tpl.text, firstName, valueStr);
          const message = aiMessage ?? templateMsg;
          const usingAi = !!aiMessage;
          // Constrói link WhatsApp robusto: usa whatsappLink se existe, senão monta com phone normalizado
          const phoneRaw = (lead.contactPhone ?? '').replace(/\D/g, '');
          const convPhone = phoneRaw ? (phoneRaw.startsWith('55') ? phoneRaw : `55${phoneRaw}`) : '';
          const convLink = convPhone ? `/inbox?id=${convPhone}` : null;
          return (
            <div className={cn(
              'rounded-md border p-2 space-y-1.5',
              usingAi
                ? 'border-purple-500/30 bg-purple-500/5'
                : 'border-emerald-500/20 bg-emerald-500/5',
            )}>
              <div className="flex items-center justify-between gap-1">
                <div className={cn(
                  'text-[10px] uppercase tracking-wider font-medium flex items-center gap-1',
                  usingAi ? 'text-purple-300' : 'text-emerald-300',
                )}>
                  {usingAi ? (
                    <><Sparkles className="h-3 w-3" /> IA gerou</>
                  ) : (
                    <><span>{tpl.emoji}</span> Template sugerido</>
                  )}
                  {lead.abandonStatus && (
                    <code className="text-[9px] bg-black/20 px-1 py-0.5 rounded ml-1">
                      {lead.abandonStatus}
                    </code>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => aiDraft.mutate()}
                    disabled={aiDraft.isPending}
                    className="text-[10px] text-purple-300 hover:text-purple-200 inline-flex items-center gap-0.5 disabled:opacity-50"
                    title="Gerar mensagem personalizada via IA"
                  >
                    {aiDraft.isPending ? (
                      <><Loader2 className="h-3 w-3 animate-spin" /> Gerando…</>
                    ) : (
                      <><Sparkles className="h-3 w-3" /> {usingAi ? 'Regerar' : 'IA escreve'}</>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(message)}
                    className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
                    title="Copiar mensagem"
                  >
                    <Copy className="h-3 w-3" /> Copiar
                  </button>
                </div>
              </div>
              <p className="text-[11px] leading-snug text-foreground/90 whitespace-pre-wrap">
                {message}
              </p>
              {aiWarning && (
                <div className="text-[9px] text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded">
                  ⚠ {aiWarning}
                </div>
              )}
              {convLink && (
                <Link
                  to={convLink}
                  onClick={() => onTouch(lead.id, 'whatsapp')}
                  className={cn(
                    'block w-full text-center px-2 py-1.5 rounded-md text-xs font-bold inline-flex items-center justify-center gap-1',
                    usingAi
                      ? 'bg-purple-500 hover:bg-purple-400 text-purple-950'
                      : 'bg-emerald-500 hover:bg-emerald-400 text-emerald-950',
                  )}
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Ver conversa no CRM
                </Link>
              )}
            </div>
          );
        })()}

        {/* Ligação — maior conversão */}
        {lead.contactPhone && (
          <a
            href={`tel:${(lead.contactPhone ?? '').replace(/\D/g, '')}`}
            onClick={() => onStartCall(lead)}
            className="block w-full text-center px-2 py-2 rounded-md bg-violet-500 hover:bg-violet-400 text-violet-950 text-sm font-bold inline-flex items-center justify-center gap-1.5"
          >
            <Phone className="h-4 w-4" /> Ligar agora ({lead.contactPhone})
          </a>
        )}

        {/* Actions secundárias */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => onTouch(lead.id, 'email')}
            className="flex-1 min-w-0 inline-flex items-center justify-center gap-1 rounded-md bg-blue-500/15 text-blue-300 hover:bg-blue-500/25 px-2 py-1.5 text-xs font-medium border border-blue-500/30"
          >
            <Mail className="h-3.5 w-3.5" />
            Email
          </button>
          <button
            onClick={() => onTouch(lead.id, 'note')}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-muted/40 text-muted-foreground hover:bg-muted px-2 py-1.5 text-xs font-medium border border-border"
            title="Registrar uma nota"
          >
            <StickyNote className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex gap-1.5">
          <button
            onClick={() => onDismiss(lead.id, 'recovered')}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-md text-[10px] py-1 text-emerald-400 hover:bg-emerald-500/10"
          >
            <Check className="h-3 w-3" /> Marcar recuperado
          </button>
          <button
            onClick={() => onDismiss(lead.id, 'lost')}
            className="flex-1 inline-flex items-center justify-center gap-1 rounded-md text-[10px] py-1 text-rose-400 hover:bg-rose-500/10"
          >
            <X className="h-3 w-3" /> Marcar perdido
          </button>
        </div>

        {lead.lastTouchAt && (
          <div className="text-[10px] text-muted-foreground text-center">
            último contato: {formatTimeSince(
              Math.floor((Date.now() - new Date(lead.lastTouchAt).getTime()) / 1000),
            )} atrás
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function Recuperacao(): JSX.Element {
  const qc = useQueryClient();
  const [stageFilter, setStageFilter] = useState<string>('');
  const [tempFilter, setTempFilter] = useState<string>('');
  const [callState, setCallState] = useState<{ callId: string; contactName: string; phone: string | null } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['recovery-queue', stageFilter, tempFilter],
    queryFn: () =>
      apiClient.recoveryQueue({
        stage: stageFilter || undefined,
        temperature: tempFilter || undefined,
      }),
    refetchInterval: 30000, // 30s — fila quente
  });

  const touchMutation = useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: 'whatsapp' | 'email' | 'call' | 'note' }) =>
      apiClient.recoveryTouch(id, { kind }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recovery-queue'] }),
  });

  const dismissMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: 'recovered' | 'lost' }) =>
      apiClient.recoveryDismiss(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recovery-queue'] });
      qc.invalidateQueries({ queryKey: ['team-overview'] });
    },
  });

  async function handleStartCall(lead: RecoveryLead): Promise<void> {
    try {
      const r = await apiClient.callStart({ contact_id: lead.contactId, lead_id: lead.id });
      setCallState({
        callId: r.call_id,
        contactName: lead.contactName ?? 'cliente',
        phone: lead.contactPhone ?? null,
      });
    } catch (err) {
      console.error('failed to start call', err);
    }
  }

  if (isLoading) {
    return <div className="p-8 text-muted-foreground">Carregando fila de recuperação...</div>;
  }

  if (isError || !data) {
    return <div className="p-8 text-rose-400">Erro: {String(error)}</div>;
  }

  const { leads, summary } = data;

  return (
    <div className="p-6 space-y-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Zap className="h-6 w-6 text-amber-400" />
            Recuperação
          </h1>
          <p className="text-sm text-muted-foreground">
            Fila priorizada · Latência sub-30s · Refresh automático 30s
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Na fila</div>
            <div className="text-2xl font-bold tabular-nums">{summary.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Checkout</div>
            <div className="text-2xl font-bold tabular-nums text-purple-400">
              {summary.checkoutCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Pgto. pendente</div>
            <div className="text-2xl font-bold tabular-nums text-amber-400">
              {summary.paymentPendingCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Pgto. falhou</div>
            <div className="text-2xl font-bold tabular-nums text-rose-400">
              {summary.paymentFailedCount}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground mb-1">Valor potencial</div>
            <div className="text-xl font-bold tabular-nums text-emerald-400">
              {formatBRL(summary.potentialValueCents)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-xs text-muted-foreground">Filtrar:</span>
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Todos os stages</option>
            <option value="checkout_started">Checkout iniciado</option>
            <option value="payment_pending">Pagamento pendente</option>
            <option value="payment_failed">Pagamento falhou</option>
          </select>
          <select
            value={tempFilter}
            onChange={(e) => setTempFilter(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value="">Todas temperaturas</option>
            <option value="quente">🔥 Quente</option>
            <option value="morno">🌡️ Morno</option>
            <option value="frio">❄️ Frio</option>
            <option value="gelado">🧊 Gelado</option>
          </select>
          {(stageFilter || tempFilter) && (
            <button
              onClick={() => {
                setStageFilter('');
                setTempFilter('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Limpar
            </button>
          )}
          {touchMutation.isPending && (
            <span className="text-xs text-emerald-400">Registrando ação...</span>
          )}
        </CardContent>
      </Card>

      {/* Grid de leads */}
      {leads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Zap className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
            <div className="text-lg font-semibold mb-1">Fila vazia 🎉</div>
            <div className="text-sm text-muted-foreground">
              Sem leads em estado de abandono no momento.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              onTouch={(id, kind) => touchMutation.mutate({ id, kind })}
              onDismiss={(id, reason) => dismissMutation.mutate({ id, reason })}
              onStartCall={handleStartCall}
            />
          ))}
        </div>
      )}

      <CallDialog
        open={!!callState}
        callId={callState?.callId ?? null}
        contactName={callState?.contactName ?? ''}
        phone={callState?.phone ?? null}
        onClose={() => {
          setCallState(null);
          qc.invalidateQueries({ queryKey: ['recovery-queue'] });
          qc.invalidateQueries({ queryKey: ['call-stats'] });
        }}
      />

      <div className="text-xs text-muted-foreground border-t border-border pt-4 flex items-center gap-2">
        <AlertTriangle className="h-3 w-3" />
        <strong>Urgência</strong> = 40% temperatura + 30% recência + 30% prob_purchase_24h.
        Em produção, queue alimentada via Redis Streams event-driven (princípio #7: sub-30s).
      </div>
    </div>
  );
}
